'use client';

import { useEffect } from 'react';

/* Adds .b-rev to every .b-section CLIENT-SIDE (so server-rendered HTML stays
   visible = fail-open), then reveals each as it enters the viewport. Sections
   already in view on mount reveal immediately; a safety net reveals everything
   after 2.6s. Honours prefers-reduced-motion. Idempotent + cleaned up for StrictMode. */
export function useScrollReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('.b-root .b-section'));
    if (!els.length) return;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return; // never hidden, nothing to reveal

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('b-rev-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );

    // Only below-fold sections get the hidden .b-rev state. Sections already in
    // view on load are left untouched — adding .b-rev then .b-rev-in in the same
    // tick still PLAYS the fade (the rect read below forces a style flush), which
    // made every refresh blink-and-fade the visible content.
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.9 && r.bottom > 0) return;
      el.classList.add('b-rev');
      io.observe(el);
    });

    const safety = window.setTimeout(() => els.forEach((el) => el.classList.add('b-rev-in')), 2600);

    return () => {
      io.disconnect();
      window.clearTimeout(safety);
    };
  }, []);
}
